import { useQuery } from '@apollo/client';
import { FileDocumentObjectTypes, FileDocumentTypes } from '@generated/graphql';
import { InlineImage } from '@ovComponents/2-component/inlineImage/inlineImage';
import { UserContext } from '@providers/userContextProvider';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { FETCH_LOGO } from './organizationLogo.query';

const LOGO_HEIGHT = '24px';

export const OrganizationLogo = () => {
  const { translateBackendString } = useLocalization();
  const { activeOrganization, organizationLoading, organizationFetchFailed } =
    useContext(UserContext);
  const theme = useTheme();
  const { t } = useTranslation('shared');

  const { data: logoData, loading: logoLoading } = useQuery(FETCH_LOGO, {
    skip: !activeOrganization.id,
    variables: {
      filter: {
        organizationId: activeOrganization.id,
        types: [FileDocumentTypes.Logo],
        objectTypes: [FileDocumentObjectTypes.Organization],
      },
    },
  });
  const documents = logoData?.fetchFileDocuments?.fileDocuments ?? [];
  const fileDocument = documents[0];

  const logoSkeleton = (
    <Skeleton
      variant='rectangular'
      width={140}
      height={LOGO_HEIGHT}
      sx={{
        borderRadius: theme.borderRadius.md,
      }}
    />
  );

  if ((organizationLoading || logoLoading) && !organizationFetchFailed)
    return logoSkeleton;

  return fileDocument && !organizationFetchFailed ? (
    <Box sx={{ height: LOGO_HEIGHT }}>
      <InlineImage
        fileDocumentId={fileDocument.id}
        style={{ maxHeight: LOGO_HEIGHT }}
        placeholder={logoSkeleton}
      />
    </Box>
  ) : (
    <Typography
      variant='bodyLarge'
      component='span'
      sx={{ color: 'inherit', display: 'block', margin: 0 }}
    >
      {organizationFetchFailed
        ? t('unknownOrganization')
        : translateBackendString(activeOrganization.translatedName)}
    </Typography>
  );
};
